import type { PrintoutSettings } from "./Header/usePrintout";

/**
 * Mirrors `PRINTOUT_AI_PROMPT_MAX_LENGTH` in `backend/models/printout_ai.py`.
 *
 * Enforced in the composer so an over-long prompt stops at the keyboard rather than after a failed
 * round trip. The server still enforces its own copy — this only saves the user the trip.
 */
export const PRINTOUT_AI_PROMPT_MAX_LENGTH = 2000;

/**
 * Printout settings the AI configurator never sees or returns.
 *
 * Two groups, for two different reasons: the DHPPD/NHPPD/actual-column/trainee flags only exist in the
 * Summary printout (the assistant is mounted in Detailed only), and `printoutColumnWidths` is owned by
 * drag-to-resize on the preview — a model rewriting pixel widths would silently undo a drag.
 */
const AI_EXCLUDED_SETTING_KEYS = [
	"showDhppd",
	"showNhppd",
	"showActualStaffColumn",
	"showActualHoursColumn",
	"hppdIncludeTrainees",
	"printoutColumnWidths",
] as const satisfies readonly (keyof PrintoutSettings)[];

/**
 * Every setting the AI endpoint exchanges, named explicitly.
 *
 * An ALLOWLIST rather than "everything except the excluded keys", because the two sides of this
 * contract are mirrored by hand. Deriving it subtractively meant a setting added to `PrintoutSettings`
 * was sent automatically while the backend's `extra="forbid"` model had never heard of it — so the
 * next new printout setting would have 422'd every request until someone updated both sides in
 * lockstep. Listed here, a new setting is simply absent from the request until it is added on
 * purpose: the assistant keeps working, it just does not know about that setting yet.
 *
 * Mirrors `DetailedPrintoutSettings` in `backend/models/printout_ai.py`. `AI_EXCLUDED_SETTING_KEYS`
 * above is what this list deliberately omits, and the type below proves the two agree.
 */
const AI_SETTING_KEYS = [
	"printGaps",
	"showRoleGroupStats",
	"dailyPrintoutGrouping",
	"divideGroupsByPage",
	"separateSheetsBy",
	"showPtoSheet",
	"showCallOutsSheet",
	"includeTimeOffReason",
	"columns",
	"sortBy",
	"staffPerRow",
	"rowGroupingBy",
	"columnGroupingBy",
	"spaceBetweenGroups",
	"orientation",
	"fitToPage",
	"fitToWidth",
	"fitToHeight",
	"hideHppdNursingHours",
	"flagNotOnboarded",
	"flagAgency",
	"mergeSimilarShiftTimes",
	"alignGroupTimesToPopular",
	"customSheetTabs",
	"showAdmins",
	"showLocationCensus",
	"customTimeGroups",
	"appendRoleToName",
	"customColumnNames",
] as const satisfies readonly (keyof PrintoutSettings)[];

/** The settings shape exchanged with the AI endpoint — the Detailed settings minus the excluded groups. */
export type AiPrintoutSettings = Pick<PrintoutSettings, (typeof AI_SETTING_KEYS)[number]>;

/**
 * Compile-time proof that the allowlist and the exclusion list are exact complements of each other.
 *
 * If someone adds a setting to `PrintoutSettings` and to neither list, this errors — which is the
 * point: it forces a decision about whether the model should see the new setting, at the moment the
 * setting is added rather than the first time a user's request fails.
 */
type UnclassifiedSetting = Exclude<keyof PrintoutSettings, (typeof AI_SETTING_KEYS)[number] | (typeof AI_EXCLUDED_SETTING_KEYS)[number]>;
const _everySettingIsClassified: UnclassifiedSetting extends never ? true : never = true;
void _everySettingIsClassified;

/** The panel's current settings as the request's `currentSettings`, with only the allowlisted keys. */
export const toAiPrintoutSettings = (settings: PrintoutSettings): AiPrintoutSettings => {
	const payload = {} as Record<string, unknown>;
	for (const key of AI_SETTING_KEYS) payload[key] = settings[key];
	return payload as AiPrintoutSettings;
};

/**
 * The settings to apply after a successful AI configure: only what the model deliberately changed,
 * laid over whatever is on screen right now.
 *
 * `changed` rather than the full `settings` document is the whole point. The server builds `settings`
 * by merging the model's patch onto the `currentSettings` captured at SUBMIT time, so applying it
 * wholesale silently reverts any setting the user toggled during the seconds the model was thinking —
 * and because the undo snapshot is taken from the same base, Undo would not bring it back either.
 *
 * The patch is still filtered through the excluded-key list: the server is not supposed to name those
 * keys, and if it ever did, letting one through would wipe a column width the user dragged by hand.
 */
export const mergeAiPrintoutSettings = (current: PrintoutSettings, changed: Partial<AiPrintoutSettings>): PrintoutSettings => {
	const applicable = { ...changed } as Partial<PrintoutSettings>;
	for (const key of AI_EXCLUDED_SETTING_KEYS) delete applicable[key];
	return { ...current, ...applicable };
};

/** How many settings the AI actually changed — reported to analytics so a no-op answer is visible. */
export const countChangedSettings = (before: PrintoutSettings, after: PrintoutSettings): number => {
	const keys = new Set<string>([...Object.keys(before), ...Object.keys(after)]);
	let changed = 0;
	for (const key of keys) {
		const beforeValue = before[key as keyof PrintoutSettings];
		const afterValue = after[key as keyof PrintoutSettings];
		// Settings are booleans, numbers, enum strings and small arrays/objects of those, so a
		// stable-enough structural compare is a JSON compare — there is nothing non-serializable in them.
		if (JSON.stringify(beforeValue ?? null) !== JSON.stringify(afterValue ?? null)) changed += 1;
	}
	return changed;
};
